/*
  moodz-cart-reconciler.js
  ────────────────────────
  Source de vérité UNIQUE pour la relation parent / enfant du panier.

  Contexte : la règle était éparpillée dans cinq fichiers et onze
  implémentations qui s'ignoraient, chacune corrigeant le panier selon sa
  propre idée. D'où des cadeaux qui disparaissaient, d'autres qui
  survivaient à leur parent, et des erreurs 400/422 dues à des écritures
  concurrentes sur des clés de ligne périmées.

  Règles appliquées, dans cet ordre :
    1. Un enfant dont le parent n'est plus au panier est supprimé.
    2. La quantité d'un CADEAU suit celle de son parent, à la hausse
       comme à la baisse.
    3. Un cadeau supprimé à la main n'est jamais recréé : on n'ajuste que
       des lignes existantes.
    4. Un upsell PAYANT n'est jamais modifié ni supprimé automatiquement.
    5. Les articles sans lien parent ne sont jamais touchés.

  Garanties :
    · état relu avant chaque décision, jamais reçu d'un appelant
    · écritures sérialisées : deux /cart/change ne partent jamais ensemble
    · convergence : aucune requête si le panier est déjà conforme

  État : ACTIF. Le module a été validé en simulation avant activation.
  Deux interrupteurs restent disponibles depuis la console, sans
  redéploiement :
    window.MOODZ_RECONCILER_DRY_RUN = true  → repasse en simulation
    window.MOODZ_RECONCILER_OFF     = true  → arrêt complet
*/
(() => {
  "use strict";

  const PROP_PARENT = "_upsell_parent_variant_id";
  const PROP_GIFT = "_moodz_is_gift";

  /* ACTIF par défaut depuis la validation en simulation.
     Deux interrupteurs restent disponibles sans redéploiement :
       window.MOODZ_RECONCILER_DRY_RUN = true  → revient en simulation
       window.MOODZ_RECONCILER_OFF     = true  → arrêt complet */
  const isDryRun = () => window.MOODZ_RECONCILER_DRY_RUN === true;
  const isOff = () => window.MOODZ_RECONCILER_OFF === true;
  const log = (...a) => console.log("[Reconciler]", ...a);

  /* Un cadeau est reconnu par son drapeau, ou par un prix nul pour les
     lignes antérieures au marquage. Le prix reste le garde-fou décisif :
     on ne modifiera jamais automatiquement une ligne payante. */
  function isGiftLine(item) {
    const props = item.properties || {};
    if (props[PROP_GIFT] === "1") return true;
    return item.price === 0;
  }

  function parentIdOf(item) {
    const props = item.properties || {};
    const v = props[PROP_PARENT];
    return v ? String(v) : null;
  }

  /* Montant réellement remisé sur la ligne. Sert à choisir QUI sacrifier
     quand il y a trop de cadeaux : on retire d'abord les lignes que le
     client paierait, jamais celles que la remise couvre déjà. */
  function remiseDe(item) {
    if (typeof item.total_discount === "number") return item.total_discount;
    return (item.line_level_discount_allocations || []).reduce(
      (somme, a) => somme + (a.amount || 0),
      0
    );
  }

  function getCart() {
    return fetch("/cart.js", { headers: { Accept: "application/json" } }).then((r) => r.json());
  }

  /* ── Plan d'action ──
     On calcule l'écart entre le panier réel et le panier voulu, puis on
     n'applique que le strict nécessaire. */
  function buildPlan(cart) {
    const items = cart.items || [];
    const actions = [];

    // Quantité disponible par variante, enfants exclus : un enfant ne peut
    // pas se servir de lui-même comme parent.
    const parentQty = {};
    items.forEach((it) => {
      if (parentIdOf(it)) return;
      const k = String(it.id);
      parentQty[k] = (parentQty[k] || 0) + it.quantity;
    });

    /* Regroupement par couple (parent, variante enfant) — et surtout PAS
       ligne par ligne. Shopify scinde une ligne quand on lui ajoute une
       unité que la remise en cours ne peut pas couvrir : on se retrouve
       avec deux lignes de la MÊME variante, propriétés identiques, l'une
       remisée et l'autre non. Raisonner ligne par ligne alignait alors
       chacune des deux sur la quantité du parent — soit le double de ce
       qu'il faut — et le doublon payant devenait permanent. */
    const groupes = {};
    items.forEach((it) => {
      const parent = parentIdOf(it);
      if (!parent) return; // règle 5
      const cle = parent + "|" + String(it.id);
      if (!groupes[cle]) groupes[cle] = [];
      groupes[cle].push(it);
    });

    /* Y a-t-il, au panier, au moins un cadeau dont le pack est encore là ?
       Toute la question des orphelins tient à ça. La remise automatique
       offre un nombre fixe d'articles et Shopify ignore nos liens
       parent-enfant : il voit des articles éligibles et des places, et
       choisit seul. Un orphelin prend donc la place d'un cadeau légitime —
       on a vu Shopify offrir les orphelins et facturer les vrais cadeaux.
       Mais seulement s'il y a concurrence. Seul au panier, un orphelin ne
       prive personne : il doit rester, à son prix d'origine, ce qui est le
       comportement voulu côté client. */
    let concurrence = false;
    items.forEach((it) => {
      const parent = parentIdOf(it);
      if (!parent || !isGiftLine(it)) return;
      if ((parentQty[parent] || 0) > 0) concurrence = true;
    });

    Object.keys(groupes).forEach((cle) => {
      const parent = cle.split("|")[0];
      const lignes = groupes[cle];
      const cible = parentQty[parent] || 0;

      /* Règle 1 — CONDITIONNELLE. Un cadeau dont le pack a quitté le panier
         reste en place et retrouve son prix : c'est la décision produit, et
         c'est lisible pour le client.
         Il n'est retiré que s'il entre en concurrence avec un cadeau
         légitime pour les places offertes par la remise (voir plus haut).
         Deux interrupteurs, sans redéploiement :
           window.MOODZ_DELETE_ORPHANS    = true → suppression systématique
           window.MOODZ_KEEP_ORPHANS_ALWAYS = true → conservation systématique */
      if (cible === 0) {
        const retirer =
          window.MOODZ_KEEP_ORPHANS_ALWAYS === true
            ? false
            : window.MOODZ_DELETE_ORPHANS === true || concurrence;
        if (retirer) {
          lignes.forEach((l) =>
            actions.push({ key: l.key, quantity: 0, why: "parent absent", id: l.id })
          );
        }
        return;
      }

      // Règle 4 — un upsell payant garde sa quantité, quoi qu'il arrive.
      const cadeaux = lignes.filter(isGiftLine);
      if (!cadeaux.length) return;

      const total = cadeaux.reduce((somme, l) => somme + l.quantity, 0);
      if (total === cible) return;

      // Règle 2 — le TOTAL des cadeaux suit la quantité du parent.
      if (total > cible) {
        /* On sacrifie d'abord les lignes les moins remisées : ce sont
           celles que le client verrait facturées. Les lignes couvertes par
           la remise sont préservées. */
        const ordre = cadeaux.slice().sort((a, b) => remiseDe(a) - remiseDe(b));
        let surplus = total - cible;
        for (let i = 0; i < ordre.length && surplus > 0; i++) {
          const l = ordre[i];
          const retire = Math.min(surplus, l.quantity);
          actions.push({
            key: l.key,
            quantity: l.quantity - retire,
            why:
              "surplus de cadeaux (" + total + " pour un parent en quantité " + cible + ")",
            id: l.id,
          });
          surplus -= retire;
        }
        return;
      }

      /* Moins de cadeaux que de parents : on complète sur la ligne la
         mieux remisée, pour ne pas créer une ligne payante de plus. */
      const porteuse = cadeaux.slice().sort((a, b) => remiseDe(b) - remiseDe(a))[0];
      actions.push({
        key: porteuse.key,
        quantity: porteuse.quantity + (cible - total),
        why: "quantité alignée sur le parent (" + total + " → " + cible + ")",
        id: porteuse.id,
      });
    });

    return actions;
  }

  /* ── File d'écriture ──
     Shopify verrouille le panier pendant une écriture : deux appels
     concurrents renvoient 400 ou 422. On les enchaîne donc strictement. */
  async function appliquerAction(a) {
    /* origFetch et non fetch : nos propres écritures ne doivent ni être
       comptées comme « en vol », ni reprogrammer une réconciliation. */
    const res = await origFetch("/cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ id: a.key, quantity: a.quantity }),
    });

    if (res.ok) return true;

    const body = await res.text().catch(() => "");
    // Ligne déjà disparue : l'objectif est atteint, ce n'est pas une panne.
    if (body.indexOf("ligne valide") !== -1 || body.indexOf("valid line") !== -1) {
      log("ligne déjà absente, on passe :", a.id);
      return false;
    }
    console.warn("[Reconciler] écriture refusée", a, body);
    return false;
  }

  function notifyUi() {
    getCart()
      .then((fresh) => {
        if (typeof window.publish === "function") {
          window.publish("cart-update", { source: "moodz-reconciler", cartData: fresh });
        }
      })
      .catch(() => {});
    if (typeof window.MOODZ_UPSELL_REFRESH === "function") window.MOODZ_UPSELL_REFRESH();
  }

  let running = false;
  let rerun = false;
  /* Nombre d'écritures panier encore en vol, toutes origines confondues.
     Un ajout d'offre, c'est une rafale : le parent puis chaque cadeau, en
     séquence. Réconcilier au milieu de cette rafale, c'est corriger un
     panier à moitié construit — et écrire sur une clé de ligne qu'un ajout
     concurrent vient d'invalider. On attend que la rafale soit finie. */
  let ecrituresEnVol = 0;

  async function reconcile(reason) {
    if (isOff()) return;
    if (running) {
      rerun = true; // un changement est survenu pendant le passage en cours
      return;
    }

    /* Une écriture est encore en vol : décider maintenant reviendrait à
       corriger un panier qui va changer, et à écrire sur des clés que cette
       requête est en train d'invalider. C'est exactement ce qui supprimait
       un cadeau légitime au milieu d'un ajout d'offre. On repasse plus tard. */
    if (ecrituresEnVol > 0) {
      schedule("écritures encore en vol");
      return;
    }

    running = true;

    try {
      let appliquees = 0;
      let echecs = 0;

      /* Relire, décider UNE action, l'appliquer, recommencer.
         Le plan n'est jamais appliqué en bloc : chaque /cart/change.js
         réussi change les clés de toutes les autres lignes. Les écritures
         suivantes partaient donc sur des clés périmées — d'où les 400 et
         le bandeau « Une erreur s'est produite lors de la mise à jour ».
         Le plafond de passages n'existe que pour borner le cas où une
         action échouerait indéfiniment. */
      for (let passage = 0; passage < 10; passage++) {
        const cart = await getCart();
        const actions = buildPlan(cart);
        if (!actions.length) break;

        if (isDryRun()) {
          log("SIMULATION (" + reason + ") — actions qui seraient appliquées :");
          actions.forEach((a) => log("   variante " + a.id + " → qté " + a.quantity + "  [" + a.why + "]"));
          log("   Simulation active — window.MOODZ_RECONCILER_DRY_RUN = false pour agir");
          break;
        }

        if (await appliquerAction(actions[0])) {
          appliquees++;
          echecs = 0;
        } else if (++echecs >= 2) {
          break;
        }
      }

      if (appliquees > 0) {
        log(appliquees + " correction(s) appliquée(s) — " + reason);
        notifyUi();
      }
    } catch (e) {
      console.warn("[Reconciler] erreur :", e);
    } finally {
      running = false;
      if (rerun) {
        rerun = false;
        setTimeout(() => reconcile("changement pendant le passage précédent"), 120);
      }
    }
  }

  /* ── Déclenchement ──
     On observe les écritures panier à la source plutôt que de dépendre
     d'un rendu : certains chemins de suppression ne passent pas par
     renderContents, et le nettoyage ne s'exécutait alors jamais.
     L'enveloppe rend la promesse d'origine telle quelle : aucun appel
     existant n'est altéré. */
  const origFetch = window.fetch;
  let debounce = null;

  /* Fenêtre large, et c'est délibéré. Ajouter une offre, c'est une rafale :
     le parent, puis chaque cadeau, en séquence. Chaque écriture repousse
     l'échéance, si bien qu'on ne réconcilie qu'une seule fois, une fois la
     rafale terminée et le panier stabilisé.
     L'ancienne fenêtre de 60 ms déclenchait une correction ENTRE deux
     ajouts : le réconciliateur voyait un surplus qui n'existait que le temps
     de la séquence, et supprimait un cadeau parfaitement légitime.
     La règle 1 étant désactivée, plus rien n'exige d'agir dans l'instant. */
  const FENETRE_MS = 800;

  function schedule(reason) {
    clearTimeout(debounce);
    debounce = setTimeout(() => reconcile(reason), FENETRE_MS);
  }

  window.fetch = function (...args) {
    const promise = origFetch.apply(this, args);
    try {
      const input = args[0];
      const url = typeof input === "string" ? input : (input && input.url) || "";
      if (/\/cart\/(add|change|update|clear)/.test(url)) {
        ecrituresEnVol++;
        promise
          .then(() => schedule("écriture panier"))
          .catch(() => {})
          .finally(() => {
            ecrituresEnVol = Math.max(0, ecrituresEnVol - 1);
          });
      }
    } catch (e) {
      /* l'observation ne doit jamais perturber l'appel d'origine */
    }
    return promise;
  };

  document.addEventListener("DOMContentLoaded", () => schedule("chargement"));
  if (document.readyState !== "loading") schedule("chargement");

  window.MOODZ_RECONCILE = (why) => reconcile(why || "appel manuel");
})();
